import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useNotificationByRef } from '../hooks/useNotifications';
import { logDebug, logError } from '../services/loggerService';

/**
 * Resolver page for `/n/<ref>` shortlinks delivered in SMS bodies (and as the
 * "view in browser" email footer).
 *
 * The SMS shortlink → secret-bearing target URL pipeline is the protocol-
 * equivalent of an email's direct secret-bearing URL: the ref is a public
 * lookup token, the secret on the resolved targetUrl is the actual auth
 * primitive. Anyone with the ref already has the SMS/email itself — same
 * threat surface either way.
 *
 * Flow:
 *   1. Page mounts at /n/<ref>. The BE endpoint GET /n/<ref> is PUBLIC —
 *      no JWT required, no profile-ownership check. It returns the
 *      NotificationSummary, which carries the secret-stamped targetUrl
 *      (e.g. /orders/<id>?s=<secret>).
 *   2. history.replace(targetUrl) — pressing back from the destination
 *      returns the user to wherever they were before the SMS click, not
 *      through /n/<ref>. A target on another origin (the www /connect page
 *      in an admin's message to a user — no secret) gets a full navigation
 *      instead, since the router can't leave the app.
 *   3. The `?s=<secret>` on the destination triggers SecretLoginHandler,
 *      which (a) calls /login-secret to mint a JWT, (b) does profile-swap
 *      if needed, (c) strips `?s=` from the URL, (d) renders the page.
 *   4. 404 / 5xx → land at home; the global error modal from apiService
 *      surfaces the cause.
 */
function hasUtmParams(target: string): boolean {
  try {
    const params = new URL(target, window.location.origin).searchParams;
    return [...params.keys()].some((key) => key.startsWith('utm_'));
  } catch {
    return false;
  }
}

/** True when the target is outside the app, e.g. `https://homeplated.com/connect?…`. */
function isOtherOrigin(target: string): boolean {
  try {
    return new URL(target, window.location.origin).origin !== window.location.origin;
  } catch {
    return false;
  }
}

export const NotificationRefPage: React.FC = () => {
  const { ref } = useParams<{ ref: string }>();
  const history = useHistory();
  const { data, error, isFetching } = useNotificationByRef(ref);

  useEffect(() => {
    if (!data) return;
    if (!data.targetUrl) {
      logDebug('NotificationRef: no targetUrl on summary', undefined, {
        category: data.category,
        event: data.event,
      });
      history.replace('/');
      return;
    }
    // Targets carrying UTM params (e.g. the inbound-SMS reply's marketing QR
    // destination) need a full page load: InitUtm reads the URL only at app
    // boot, so an in-app replace would silently drop the campaign attribution.
    if (hasUtmParams(data.targetUrl)) {
      logDebug('NotificationRef: full navigation to UTM targetUrl', undefined, {
        ref: data.ref,
        channel: data.channel,
      });
      window.location.replace(data.targetUrl);
      return;
    }
    // history.replace treats an absolute URL as an in-app path, so a target
    // on another origin needs a full page load too.
    if (isOtherOrigin(data.targetUrl)) {
      logDebug('NotificationRef: full navigation to off-app targetUrl', undefined, {
        ref: data.ref,
        channel: data.channel,
      });
      window.location.replace(data.targetUrl);
      return;
    }
    logDebug('NotificationRef: navigating to targetUrl', undefined, {
      ref: data.ref,
      channel: data.channel,
    });
    history.replace(data.targetUrl);
  }, [data, history]);

  useEffect(() => {
    if (!error) return;
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'NotificationRef: failed to resolve',
      undefined,
      { ref, status: (error as { status?: number }).status },
    );
    history.replace('/');
  }, [error, history, ref]);

  return (
    <IonPage>
      <IonContent>
        <div className="flex h-full items-center justify-center">
          {(isFetching || !!data) && <IonSpinner name="crescent" />}
        </div>
      </IonContent>
    </IonPage>
  );
};
